import { ErrorReporter } from '@wix/editor-error-reporter';
import { KitDefinition } from '@wix/editor-kits';
import type { HttpClient } from '@wix/http-client';
import _ from 'lodash';
import { ChatControllerBiEventNumbers } from '../chatController/constants';
import { getPreferredPresets } from '../chatController/utils';
import { SiteGenerator } from '../siteGenerator';
import {
  FedopsInteraction,
  IKitToUseService,
  PredictKitParams,
  SendBi,
} from '../types';
import { fetchTheme, predictKitPromptHub } from './kitsAPI';
import { FALLBACK_KIT_ID, PROMPT_ID } from './kitsConstants';

export class KitsToUseServiceWithPrompt implements IKitToUseService {
  private client: HttpClient;
  private kitDefinitions: KitDefinition[];
  private kitsToUse: KitDefinition[] = [];
  private currentKitIndex = 0;
  private siteGenerator: SiteGenerator;
  private sendBi: SendBi;
  private attempt = 1;
  private isKitGeneratedSuccessfully = true;
  public theme: string;
  private businessType: string = '';
  private readonly interactionStarted: FedopsInteraction;
  private readonly interactionEnded: FedopsInteraction;
  private featureName?: string;

  constructor(
    client: HttpClient,
    siteGenerator,
    sendBi,
    interactionStarted,
    interactionEnded,
    featureName?: string,
  ) {
    this.client = client;
    this.siteGenerator = siteGenerator;
    this.sendBi = sendBi;
    this.interactionStarted = interactionStarted;
    this.interactionEnded = interactionEnded;
    this.featureName = featureName;
  }

  private getDescription() {
    return this.siteGenerator.getUserData().description as string;
  }

  private getBusinessType() {
    return this.siteGenerator.getUserData().businessTypeName;
  }

  private async setTheme() {
    try {
      this.theme = await fetchTheme(this.client, this.sendBi);
    } catch (error: unknown) {
      this.theme = '';
    }

    this.sendBi(ChatControllerBiEventNumbers.PHOTO_THEME_INPUT, {
      description: this.theme,
      params: JSON.stringify({
        ...this.siteGenerator.getUserData(),
      }),
    });
  }

  private get kitDescriptions() {
    return this.kitDefinitions
      .filter((item) => item.isPublished)
      .map((item) => ({
        [item.title]: item.descriptionForPhotoTheme,
      }));
  }

  private async getKitIds({
    params: { businessTerm, businessDescription },
    withRetry = true,
  }: PredictKitParams): Promise<string[]> {
    try {
      this.interactionStarted('sg_predict_kits');
      const {
        response: { generatedTexts },
      } = await predictKitPromptHub({
        client: this.client,
        params: {
          theme: this.theme,
          businessTerm,
          businessDescription,
          kitDescriptions: JSON.stringify(this.kitDescriptions),
        },
        sendBi: this.sendBi,
        featureName: this.featureName,
      });
      const predictedKits = Object.values(
        JSON.parse(generatedTexts[0]) as object,
      );
      this.isKitGeneratedSuccessfully = true;
      this.interactionEnded('sg_predict_kits');

      return predictedKits;
    } catch (error: unknown) {
      this.attempt += 1;
      this.isKitGeneratedSuccessfully = false;

      ErrorReporter.captureException(error, {
        tags: {
          siteGenerationAiErrorResponse: true,
        },
        extra: {
          businessTerm,
          theme: this.theme,
          withRetry,
          kitDescriptions: this.kitDescriptions,
        },
      });

      if (!withRetry) {
        return [FALLBACK_KIT_ID];
      }

      return this.getKitIds({
        params: { businessTerm, businessDescription },
        withRetry: false,
      });
    }
  }

  private async setKitsToUse() {
    const kitIds = await this.getKitIds({
      params: {
        businessTerm: this.businessType,
        businessDescription: this.getDescription(),
      },
    });
    this.kitsToUse = kitIds.map(
      (kitId) =>
        this.kitDefinitions.find((item) => item.title === kitId) ||
        this.kitDefinitions.find((item) => item.title === FALLBACK_KIT_ID) ||
        (_.sample(getPreferredPresets(this.kitDefinitions)) as KitDefinition),
    );
  }

  public async init(kitDefinition: KitDefinition[] = this.kitDefinitions) {
    this.currentKitIndex = 0;
    this.kitDefinitions = kitDefinition;
    this.businessType = this.getBusinessType();
    await this.setTheme();
    await this.setKitsToUse();
  }

  private reportKitSelected() {
    this.sendBi(ChatControllerBiEventNumbers.KIT_GENERATED, {
      selectionList: JSON.stringify({
        generated: this.kitsToUse.map((item) => item.title),
        selected: this.kitsToUse[this.currentKitIndex].title,
      }),
      prompt: PROMPT_ID,
      attempt: this.attempt,
    });
  }

  public getInitialKit() {
    this.reportKitSelected();

    return this.kitsToUse[this.currentKitIndex];
  }

  public async getRegenerateKit() {
    if (
      !this.isKitGeneratedSuccessfully ||
      this.businessType !== this.getBusinessType()
    ) {
      await this.init();
    } else {
      this.currentKitIndex += 1;
    }

    if (this.currentKitIndex > this.kitsToUse.length - 1) {
      this.currentKitIndex = 0;
    }

    this.reportKitSelected();

    return this.kitsToUse[this.currentKitIndex];
  }
}
